/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes */
import EmberObject, { computed, set } from '@ember/object';
import { A } from '@ember/array';

import * as StageUtils from '../utils/stage';
import * as SliderUtils from '../utils/slider';
import { computeCompensateLoop } from '../../utils/common';
import * as ConversionUtils from '../utils/conversion';
import * as ArrayUtils from '../utils/array';
import * as CommonUtils from '../../utils/common';
import FilterConstants from '../../filter-constants';
import Noise from './sub-results/noise';
import Power from './sub-results/power';
import VoltageRange from './sub-results/voltage-range';
import FirstOrderTargetStage from '../stages/first-order';
import FirstOrderActualStage from '../stages/actual-first-order';
import SecondOrderActualStage from '../stages/actual-second-order';
import Optimization from '../optimization/optimization';
import Series from '../tolerances/series';
import StageDetails from './sub-results/stage-details';
import CircuitDetails from './sub-results/circuit-details';
import Frequency from '../process-data/frequency';
import CircuitGroupResults from './circuit-group-results';
import NoiseInput from '../inputs/noise';
import * as ResultsUtils from '../utils/results';

export default class ComponentSelectionResults extends EmberObject {
  filter = null;
  specifications = null;
  componentsInput = null;
  noiseInput = null;
  opAmpSource = null;
  stageOrder = null;
  specificationsInput = null;
  stopbandFrequency = null;
  frequencyArray = null;
  friendlyFrequencyArray = null;
  filterFrequencyRange = null;
  timeAxis = null;
  targetStages = null;
  nthOrder = null;
  responseType = null;
  filterResponseValue = null;
  init() {
    super.init();

    if (CommonUtils.isNullOrUndefined(this.noiseInput)) {
      var filter = this.filter;
      var filterFrequencyRange = this.specifications.filterFrequencyRange;
      var stopFrequency = filterFrequencyRange.stopFrequency;
      var adcBandwidth = filter.getAdcFrequency(stopFrequency);
      var noiseInput = new NoiseInput({ adcBandwidth: adcBandwidth });
      set(this, 'noiseInput', noiseInput);
    }

    if (!CommonUtils.isNullOrUndefined(this.specifications)) {
      set(this, 'specificationsInput', this.specifications.specificationsInput);
      set(this, 'stopbandFrequency', this.specifications.specificationsInput.stopbandFrequency);
      set(this, 'frequencyArray', this.specifications.frequencyArray);
      set(this, 'friendlyFrequencyArray', this.specifications.friendlyFrequencyArray);
      set(this, 'filterFrequencyRange', this.specifications.filterFrequencyRange);
      set(this, 'timeAxis', this.specifications.timeAxis);
      set(this, 'targetStages', this.specifications.targetStagesWithDistributedGain);
      this.nthOrder = this.specifications.nthOrder;
      this.responseType = this.specificationsInput.responseType;
      this.filterResponseValue = this.specificationsInput.filterResponseValue;
    }
  }

  @computed('componentsInput.optimization', 'stopbandFrequency') get optimization() {
    var stopbandFrequency = this.stopbandFrequency;
    var result = Optimization.create({
      optimization: this.componentsInput.optimization,
      stopbandFrequency: stopbandFrequency,
    });
    return result;
  }

  @computed(
    'componentsInput.{compensateForGbw,individualStageInputs,optimizationPreference}',
    'filterFrequencyRange',
    'frequencyArray',
    'opAmpSource',
    'optimization',
    'stopbandFrequency',
    'targetStages',
  )
  get actualStages() {
    var individualStagesInput = this.componentsInput.individualStageInputs;
    var stopbandFrequency = this.stopbandFrequency;
    var targetStages = this.targetStages;
    var opAmpSource = this.opAmpSource;
    var filterFrequencyRange = this.filterFrequencyRange;
    var frequencyArray = this.frequencyArray;
    //var stageFpArray = targetStages.map((item) => { return item.stageFp });
    var optimizationPreference = this.componentsInput.optimizationPreference;
    var componentsInput = this.componentsInput;

    var sortOrder = componentsInput.sortOrder;

    //initialize sort order if not available
    if (sortOrder.length === 0) {
      for (let i = 0; i < targetStages.length; i++) {
        sortOrder.push(i);
      }
    }

    var hasImaginaryOpAmp = false;
    var stageNumber = 0;
    var mappedStages = sortOrder.map((sortIndex) => {
      stageNumber = stageNumber++;
      var compensateLoop = computeCompensateLoop(this.componentsInput.compensateForGbw);
      var opAmp = individualStagesInput[sortIndex].opAmp;

      if (opAmp === FilterConstants.idealOpAmp) {
        hasImaginaryOpAmp = true;
      }

      var optimization = this.optimization;
      var masterCapacitorIndex = individualStagesInput[sortIndex].componentSizing.value;
      var r3Index = individualStagesInput[sortIndex].gainRatio.value;
      var implementation = individualStagesInput[sortIndex].implementation;
      var capacitorValues = SliderUtils.generateCapacitorRange(Series.eSeries24);
      var gainValues = SliderUtils.generateGainRange(Series.eSeries96);
      var masterCapacitorValue = capacitorValues[masterCapacitorIndex];
      var r3Value = gainValues[r3Index];

      var res = null;
      var actualStagesPropertiesHash = {
        targetStage: targetStages[sortIndex],
        selectedImplementation: implementation,
        compensateLoop: compensateLoop,
        opAmp: opAmp,
        optimization: optimization,
        masterCapacitorValue: masterCapacitorValue,
        r3Value: r3Value,
        stopbandFrequency: stopbandFrequency,
        componentsInput: componentsInput,
        opAmpSource: opAmpSource,
      };
      if (targetStages[sortIndex] instanceof FirstOrderTargetStage) {
        res = FirstOrderActualStage.create(actualStagesPropertiesHash);
      } else {
        res = SecondOrderActualStage.create(actualStagesPropertiesHash);
      }

      return res;
    });

    if (optimizationPreference === FilterConstants.optimizationPreferenceOptions.nonSpecific && !hasImaginaryOpAmp) {
      let optimizedStages = [];
      optimizedStages = this.optimization.optimizeStageOrder(mappedStages, filterFrequencyRange);
      if (optimizedStages.length > 0) {
        for (let i = 0; i < mappedStages.length; i++) {
          set(optimizedStages[i], 'frequencyArray', frequencyArray);
          set(optimizedStages[i].circuit, 'frequencyArray', frequencyArray);
        }
        mappedStages = optimizedStages;
      }
    }

    var result = StageUtils.setPreviousAndNextStage(mappedStages);
    return result;
  }

  @computed('actualStages') get circuits() {
    var result = this.actualStages.map((item) => {
      return item.circuit;
    });
    return result;
  }
  @computed('stageGroup.filter.name') get filterName() {
    return this.stageGroup.filter.name;
  }

  @computed('actualStages', 'friendlyFrequencyArray', 'noiseInput.adcBandwidth') get circuitGroupResults() {
    var adcBandwidth = this.noiseInput.adcBandwidth;
    var frequencyArray = this.friendlyFrequencyArray;
    var actualStages = this.actualStages;
    var circuitResults = CircuitGroupResults.create({
      actualStages: actualStages,
      adcBandwidth: adcBandwidth,
      frequencyArray: frequencyArray,
    });
    return circuitResults;
  }

  @computed(
    'filterFrequencyRange.{pointsPerDecade,startFrequency,stopFrequency}',
    'frequencyArray',
    'noiseInput.adcBandwidth',
  )
  get noiseFrequencyArray() {
    const startFrequency = this.filterFrequencyRange.startFrequency;
    const stopFrequency = this.filterFrequencyRange.stopFrequency;
    const pointsPerDecade = this.filterFrequencyRange.pointsPerDecade;
    const adcBandwidth = Math.max(this.noiseInput.adcBandwidth * 10, stopFrequency);
    const decade = Math.log10(stopFrequency) - Math.log10(startFrequency);

    var result = Frequency.create({
      startFrequency: startFrequency,
      stopFrequency: adcBandwidth,
      pointsPerDecade: pointsPerDecade,
      decade: decade,
    }).friendlyFrequencyArray;
    return result;
  }

  @computed('actualStages', 'noiseFrequencyArray', 'noiseInput.adcBandwidth') get noiseCircuitGroupResults() {
    var adcBandwidth = this.noiseInput.adcBandwidth;
    var frequencyArray = this.noiseFrequencyArray;
    var actualStages = this.actualStages;
    var circuitResults = CircuitGroupResults.create({
      actualStages: actualStages,
      adcBandwidth: adcBandwidth,
      frequencyArray: frequencyArray,
    });
    return circuitResults;
  }

  @computed('actualStages', 'componentsInput.vsMinus') get needReferenceStage() {
    var vsMinus = this.componentsInput.vsMinus;
    var result = false;
    if (vsMinus === 0) {
      var actualStages = A(this.actualStages);
      result = actualStages.any((stage) => {
        return stage.circuit.needReferenceStage;
      });
    }

    return result;
  }

  @computed('circuitGroupResults', 'friendlyFrequencyArray', 'specificationsInput') get magnitudeDb() {
    var circuitGroupResults = this.circuitGroupResults;
    var frequencyArray = this.friendlyFrequencyArray;
    var magnitude = circuitGroupResults.magnitude;
    var dbData = ConversionUtils.VVarrtoDBarr(magnitude);
    var dataPoints = ArrayUtils.joinArrays(frequencyArray, dbData);
    var limits = ResultsUtils.getRangeLimits(dataPoints);

    var specInputs = this.specificationsInput;
    var gainDB =
      specInputs.gainUnit === FilterConstants.gainUnits.db ? specInputs.gain : ConversionUtils.VVtoDB(specInputs.gain);
    limits.ymin = gainDB + (specInputs.stopbandAttenuation - 20);
    limits.ymax = gainDB + 10;

    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('circuitGroupResults', 'friendlyFrequencyArray') get magnitudeVV() {
    var circuitGroupResults = this.circuitGroupResults;
    var magnitude = circuitGroupResults.magnitude;
    var frequencyArray = this.friendlyFrequencyArray;
    var dataPoints = ArrayUtils.joinArrays(frequencyArray, magnitude);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    limits.ymax += 0.2; // give 0.2 V/V headroom
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('circuitGroupResults', 'friendlyFrequencyArray') get phaseDegrees() {
    var circuitGroupResults = this.circuitGroupResults;
    var frequencyArray = this.friendlyFrequencyArray;
    var phaseArray = circuitGroupResults.phase;
    var dbData = ConversionUtils.radianArrayToDegreeArray(phaseArray);
    var dataPoints = ArrayUtils.joinArrays(frequencyArray, dbData);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var newYmax = limits.ymax + (limits.ymax - limits.ymin) / 10;
    var newYmin = limits.ymin - (limits.ymax - limits.ymin) / 10;
    limits.ymax = newYmax;
    limits.ymin = newYmin;
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('circuitGroupResults', 'friendlyFrequencyArray') get phaseRadians() {
    var circuitGroupResults = this.circuitGroupResults;
    var frequencyArray = this.friendlyFrequencyArray;
    var phaseArray = circuitGroupResults.phase;
    var dataPoints = ArrayUtils.joinArrays(frequencyArray, phaseArray);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var newYmax = limits.ymax + (limits.ymax - limits.ymin) / 10;
    var newYmin = limits.ymin - (limits.ymax - limits.ymin) / 10;
    limits.ymax = newYmax;
    limits.ymin = newYmin;
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('circuitGroupResults', 'friendlyFrequencyArray') get groupDelay() {
    var circuitGroupResults = this.circuitGroupResults;
    var phaseArray = circuitGroupResults.phase;
    var narrowFreqArray = this.friendlyFrequencyArray;
    var { groupDelayArray, newFrequencyAxis } = ConversionUtils.groupDelay(phaseArray, narrowFreqArray);
    var dataPoints = ArrayUtils.joinArrays(newFrequencyAxis, groupDelayArray);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    limits.ymax = limits.ymax + (limits.ymax - limits.ymin) / 10;
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('circuitGroupResults', 'friendlyFrequencyArray') get phaseDelay() {
    var circuitGroupResults = this.circuitGroupResults;
    var phaseArray = circuitGroupResults.phase;
    var narrowFreqArray = this.friendlyFrequencyArray;
    var dbData = ConversionUtils.phaseDelay(phaseArray, narrowFreqArray);
    var dataPoints = ArrayUtils.joinArrays(narrowFreqArray, dbData);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    limits.ymax = limits.ymax + (limits.ymax - limits.ymin) / 10;

    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('circuitGroupResults', 'timeAxis') get stepResponse() {
    var circuitGroupResults = this.circuitGroupResults;
    var stepResponse = circuitGroupResults.stepResponse;
    var timeAxis = this.timeAxis;
    var dataPoints = ArrayUtils.joinArrays(timeAxis, stepResponse);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var overreach = (limits.ymax - limits.ymin) / 10;
    limits.ymax = limits.ymax + overreach;
    if (limits.ymin < 0) {
      limits.ymin = limits.ymin - overreach;
    }
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('circuitGroupResults.circuitResultsSorted', 'friendlyFrequencyArray') get inputImpedanceMagnitude() {
    var firstCircuitResult = this.circuitGroupResults.circuitResultsSorted[0];
    var dbData = firstCircuitResult.inputImpedanceMagnitude;
    var frequencyArray = this.friendlyFrequencyArray;
    var dataPoints = ArrayUtils.joinArrays(frequencyArray, dbData);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('circuitGroupResults.circuitResultsSorted', 'friendlyFrequencyArray') get inputImpedancePhase() {
    var firstCircuitResult = this.circuitGroupResults.circuitResultsSorted[0];
    var dbData = firstCircuitResult.inputImpedancePhase;
    var frequencyArray = this.friendlyFrequencyArray;
    var dataPoints = ArrayUtils.joinArrays(frequencyArray, dbData);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('friendlyFrequencyArray', 'noiseCircuitGroupResults', 'noiseFrequencyArray', 'noiseInput.adcBandwidth')
  get noise() {
    var frequencyArray = this.friendlyFrequencyArray;
    var noiseFrequencyArray = this.noiseFrequencyArray;
    var adcBandwidth = this.noiseInput.adcBandwidth;
    var noiseCircuitGroupResults = this.noiseCircuitGroupResults;
    var lastCircuitNoiseGroup = noiseCircuitGroupResults.lastCircuitNoiseGroup;
    var nominalNoiseArray = lastCircuitNoiseGroup.totalSpectralNoiseReferredToOutputArray;
    var result = Noise.create({
      nominalNoiseArray: nominalNoiseArray,
      frequencyArray: frequencyArray,
      noiseFrequencyArray: noiseFrequencyArray,
      adcBandwidth: adcBandwidth,
    });
    return result;
  }

  @computed('actualStages', 'needReferenceStage', 'componentsInput') get power() {
    var actualStages = this.actualStages;
    var componentsInput = this.componentsInput;
    var needReferenceStage = this.needReferenceStage;
    var result = Power.create({
      stages: actualStages,
      componentsInput: componentsInput,
      needReferenceStage: needReferenceStage,
    });
    return result;
  }

  @computed('circuitGroupResults', 'componentsInput.{vsMinus,vsPlus}', 'frequencyArray') get voltageRange() {
    var vsMinus = this.componentsInput.vsMinus;
    var vsPlus = this.componentsInput.vsPlus;
    var circuitGroupResults = this.circuitGroupResults;
    var circuitResults = circuitGroupResults.circuitResults;
    var result = VoltageRange.create({
      circuitResults: circuitResults,
      vsMinus: vsMinus,
      vsPlus: vsPlus,
      toleranceInput: false,
      worstCaseMagnitudes: null,
    });
    return result;
  }

  @computed('actualStages', 'magnitudeDb.limits') get detailsOfStages() {
    var limMagnitudeDb = this.magnitudeDb.limits;
    var actualStages = this.actualStages;
    var result = actualStages.map((actualStage) => {
      var graphData = ResultsUtils.getGraphData(actualStage);
      graphData.limits = limMagnitudeDb;
      var targetStage = actualStage.targetStage;
      var res = StageDetails.create({ targetStage: targetStage, actualStage: actualStage, graphData: graphData });
      return res;
    });
    return result;
  }

  @computed('circuits', 'actualStages', 'componentsInput') get detailsOfCircuits() {
    var circuits = this.circuits;
    var stages = this.actualStages;
    var componentsInput = this.componentsInput;
    var result = circuits.map((circuit, index) => {
      var actualStage = stages[index];
      return CircuitDetails.create({ circuit: circuit, stage: actualStage, componentsInput: componentsInput });
    });
    return result;
  }

  @computed('componentsInput') get powerSpan() {
    var componentsInput = this.componentsInput;
    return componentsInput.vsPlus - componentsInput.vsMinus;
  }

  getStageLimits(index) {
    var stages = this.actualStages;
    var allowedLimits = stages[index].allowedLimits;
    var recommendedLimits = stages[index].recommendedLimits;

    var result = {
      allowedLimits: allowedLimits,
      recommendedLimits: recommendedLimits,
      powerSpan: this.powerSpan,
    };

    return result;
  }

  @computed('actualStages') get noiseResistance() {
    var actualStages = this.actualStages;
    var lastStage = A(actualStages).lastObject;
    var highVNoiseCircuit = lastStage.highVNoiseCircuit;
    var highINoiseCircuit = lastStage.highINoiseCircuit;
    var spectralDensityHighVNoise = highVNoiseCircuit.noiseGroup.totalSpectralNoiseReferredToOutputArray;
    var spectralDensityHighINoise = highINoiseCircuit.noiseGroup.totalSpectralNoiseReferredToOutputArray;
    var length = spectralDensityHighVNoise.length;
    var averageResistance = null;
    for (var i = 0; i < length; i++) {
      var noiseResistance = spectralDensityHighINoise[i] / spectralDensityHighVNoise[i];
      averageResistance += noiseResistance / length;
    }

    return averageResistance;
  }

  @computed('circuitGroupResults.overallLimits') get overallLimits() {
    return this.circuitGroupResults.overallLimits;
  }
}
